import { useEffect, useRef, useState } from "react";
import { Check, CircleHelp, Heart, Star, X } from "lucide-react";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import {
  HIGHLIGHTER_OPACITY,
  MAX_TEXT_LENGTH,
  STROKE_WIDTH,
  appendPoint,
  arrowHead,
  hitsStroke,
  isInkTool,
  isShapeTool,
  shapeBox,
  strokePath,
  toPagePoint,
  type AnnotationTool,
  type LiveChunk,
  type LiveTool,
  type StampKind,
  type Stroke,
} from "@/features/meeting/services/annotation-geometry";
import { LASER_EXPIRY_MS } from "@/features/meeting/store/meeting-annotation.store";

/** How far from the eraser's centre a mark is caught, as a fraction of the page's width. */
const ERASER_REACH = 0.012;

/** How often a mark in progress is sent to the room: ~20 times a second. */
const LIVE_INTERVAL_MS = 50;

/** A laser's live id is renewed after this many points, inside the server's per-stroke limit. */
const LASER_ROTATE_POINTS = 1_500;

/** A drag shorter than this (page widths) is a click, not a shape. */
const MIN_SHAPE = 0.005;

/** A mark someone is still making, as the layer draws it. */
type Draft = Omit<Stroke, "tool" | "by"> & { tool: LiveTool };

interface AnnotationLayerProps {
  /** 1-based page this layer covers. */
  page: number;
  /** The page's height over its width. */
  ratio: number;
  /** This page's marks, oldest first. */
  strokes: Stroke[];
  /** Marks other people are making on this page right now, and their lasers. */
  drafts?: Draft[];
  tool: AnnotationTool;
  /** Which stamp the stamp tool places. */
  stamp: StampKind;
  color: string;
  /** A mark was finished on this page. */
  onStroke: (stroke: Omit<Stroke, "by">) => void;
  /** The mark being made changed — for the room to watch it happen. */
  onLive?: (chunk: LiveChunk) => void;
  /** The eraser touched these marks. */
  onErase: (ids: string[]) => void;
}

/**
 * The marks over one page of a shared document: an SVG the exact size of the
 * page, drawn in page-relative units (see annotation-geometry), so it follows
 * the page through every zoom and window size.
 *
 * With the select tool it lets every pointer through to the document, which
 * scrolls and zooms as if the layer were not there. With any other tool it
 * takes the pointer — and `touch-action: none` stops a finger's stroke from
 * also scrolling the page under it.
 */
export function AnnotationLayer({
  page,
  ratio,
  strokes,
  drafts,
  tool,
  stamp,
  color,
  onStroke,
  onLive,
  onErase,
}: AnnotationLayerProps) {
  const t = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  // The mark being made. Kept here, not in the store, until it is finished:
  // half a line is nobody else's business except as a live preview.
  const [draft, setDraft] = useState<number[] | null>(null);
  const draftRef = useRef<number[] | null>(null);
  // The mark's id, made when it starts, so its live updates and the finished
  // mark are recognisably the same one.
  const draftIdRef = useRef("");
  // How many of the draft's points have gone out live, and when.
  const liveSentRef = useRef({ points: 0, at: 0 });
  const erasedRef = useRef<Set<string>>(new Set());
  const lastEraseRef = useRef<[number, number] | null>(null);
  // Text being typed: where it starts on the page, and what is typed so far.
  const [typing, setTyping] = useState<{ x: number; y: number; value: string } | null>(null);
  // This client's own laser trail, and the timer that fades it once it stops.
  const [laser, setLaser] = useState<number[]>([]);
  const laserRef = useRef({ id: "", sent: 0, at: 0 });
  const laserFade = useRef<number | null>(null);
  useEffect(() => () => {
    if (laserFade.current !== null) window.clearTimeout(laserFade.current);
  }, []);

  const ink = isInkTool(tool);
  const shape = isShapeTool(tool);
  const active = tool !== "select";

  // Switching tools mid-way drops what was in progress.
  useEffect(() => {
    draftRef.current = null;
    setDraft(null);
    setTyping(null);
    setLaser([]);
  }, [tool]);

  const rect = () => svgRef.current!.getBoundingClientRect();

  // ── Eraser ──
  // Tested along the path between pointer samples, not only at them: a quick
  // swipe reports points far apart, and a line lying between two of them
  // would survive being crossed.
  const erase = (x: number, y: number) => {
    const [fx, fy] = lastEraseRef.current ?? [x, y];
    lastEraseRef.current = [x, y];
    const steps = Math.max(1, Math.ceil(Math.hypot(x - fx, (y - fy) * ratio) / (ERASER_REACH / 2)));
    const hit = strokes
      .filter((st) => {
        if (erasedRef.current.has(st.id)) return false;
        for (let i = 1; i <= steps; i++) {
          const k = i / steps;
          if (hitsStroke(st, fx + (x - fx) * k, fy + (y - fy) * k, ratio, ERASER_REACH)) return true;
        }
        return false;
      })
      .map((st) => st.id);
    if (hit.length === 0) return;
    hit.forEach((id) => erasedRef.current.add(id));
    onErase(hit);
  };

  // ── Live updates ──
  const sendLive = (pts: number[], now: number) => {
    const sent = liveSentRef.current;
    if (!onLive || now - sent.at < LIVE_INTERVAL_MS) return;
    const liveTool = tool as LiveTool;
    if (shape) {
      // A shape is redrawn whole each time: its two corners, from the start.
      onLive({ id: draftIdRef.current, page, tool: liveTool, color, width: STROKE_WIDTH[liveTool], from: 0, points: pts });
      liveSentRef.current = { points: 2, at: now };
      return;
    }
    if (pts.length / 2 <= sent.points) return;
    onLive({
      id: draftIdRef.current,
      page,
      tool: liveTool,
      color,
      width: STROKE_WIDTH[liveTool],
      from: sent.points,
      points: pts.slice(sent.points * 2),
    });
    liveSentRef.current = { points: pts.length / 2, at: now };
  };

  const moveLaser = (x: number, y: number, now: number) => {
    setLaser((trail) => [...trail, x, y].slice(-24 * 2));
    // Gone once it stops moving, the same moment everyone else's copy goes.
    if (laserFade.current !== null) window.clearTimeout(laserFade.current);
    laserFade.current = window.setTimeout(() => setLaser([]), LASER_EXPIRY_MS);
    const l = laserRef.current;
    if (!l.id || l.sent >= LASER_ROTATE_POINTS) laserRef.current = { id: crypto.randomUUID(), sent: 0, at: 0 };
    if (!onLive || now - laserRef.current.at < LIVE_INTERVAL_MS) return;
    onLive({
      id: laserRef.current.id,
      page,
      tool: "laser",
      color,
      width: STROKE_WIDTH.laser,
      from: laserRef.current.sent,
      points: [x, y],
    });
    laserRef.current = { ...laserRef.current, sent: laserRef.current.sent + 1, at: now };
  };

  // ── Pointer ──
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!active || (e.pointerType === "mouse" && e.button !== 0)) return;
    const [x, y] = toPagePoint(e.clientX, e.clientY, rect());
    if (tool === "text") {
      e.preventDefault();
      // A click while typing finishes the text; the next click starts a new one.
      if (typing) commitText();
      else setTyping({ x, y, value: "" });
      return;
    }
    e.preventDefault();
    if (tool === "stamp") {
      onStroke({ id: crypto.randomUUID(), page, tool: "stamp", stamp, color, width: STROKE_WIDTH.stamp, points: [x, y] });
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    if (tool === "laser") {
      moveLaser(x, y, performance.now());
      return;
    }
    if (tool === "eraser") {
      erasedRef.current = new Set();
      lastEraseRef.current = null;
      erase(x, y);
      draftRef.current = [];
      return;
    }
    draftRef.current = shape ? [x, y, x, y] : [x, y];
    draftIdRef.current = crypto.randomUUID();
    liveSentRef.current = { points: 0, at: 0 };
    setDraft(draftRef.current);
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    // The laser follows a mouse without a button held; a finger has no hover,
    // so for touch it follows while pressed.
    if (tool === "laser") {
      if (e.pointerType !== "mouse" && e.buttons === 0) return;
      moveLaser(...toPagePoint(e.clientX, e.clientY, rect()), performance.now());
      return;
    }
    if (!draftRef.current) return;
    // Coalesced events are the samples the browser merged into this one; a
    // fast stroke reads as a polygon without them.
    const samples = e.nativeEvent.getCoalescedEvents?.() ?? [];
    const events = samples.length > 0 ? samples : [e.nativeEvent];
    const r = rect();
    if (tool === "eraser") {
      for (const ev of events) erase(...toPagePoint(ev.clientX, ev.clientY, r));
      return;
    }
    let pts = draftRef.current;
    if (shape) {
      const last = events[events.length - 1];
      const [x, y] = toPagePoint(last.clientX, last.clientY, r);
      pts = [pts[0], pts[1], x, y];
    } else {
      for (const ev of events) pts = appendPoint(pts, ...toPagePoint(ev.clientX, ev.clientY, r), ratio);
    }
    if (pts !== draftRef.current) {
      draftRef.current = pts;
      setDraft(pts);
      sendLive(pts, performance.now());
    }
  };

  const finish = () => {
    const pts = draftRef.current;
    draftRef.current = null;
    setDraft(null);
    if (tool === "laser") {
      // A touch laser goes when the finger lifts; a mouse one when it leaves.
      return;
    }
    if (!pts || pts.length < 2) return;
    if (ink) {
      onStroke({ id: draftIdRef.current, page, tool, color, width: STROKE_WIDTH[tool], points: pts });
    } else if (shape && Math.hypot(pts[2] - pts[0], (pts[3] - pts[1]) * ratio) >= MIN_SHAPE) {
      onStroke({ id: draftIdRef.current, page, tool, color, width: STROKE_WIDTH[tool], points: pts });
    }
  };

  const commitText = () => {
    const text = (typing?.value ?? "").trim().slice(0, MAX_TEXT_LENGTH);
    if (typing && text) {
      onStroke({
        id: crypto.randomUUID(),
        page,
        tool: "text",
        text,
        color,
        width: STROKE_WIDTH.text,
        points: [typing.x, typing.y],
      });
    }
    setTyping(null);
  };

  const cursor =
    tool === "eraser" ? "cell" : tool === "text" ? "text" : tool === "laser" ? "none" : active ? "crosshair" : undefined;

  const ownDraft: Draft | null =
    draft && (ink || shape)
      ? { id: "draft", page, tool: tool as LiveTool, color, width: STROKE_WIDTH[tool as LiveTool], points: draft }
      : null;

  return (
    <>
      <svg
        ref={svgRef}
        viewBox={`0 0 1 ${ratio}`}
        className="absolute inset-0 h-full w-full"
        style={{
          pointerEvents: active ? "auto" : "none",
          touchAction: active ? "none" : "auto",
          cursor,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finish}
        onPointerCancel={finish}
        onPointerLeave={() => tool === "laser" && setLaser([])}
        aria-hidden="true"
      >
        {strokes.map((st) => (
          <Mark key={st.id} mark={st} ratio={ratio} />
        ))}
        {drafts?.map((st) =>
          st.tool === "laser" ? (
            <LaserTrail key={`live-${st.id}`} points={st.points} ratio={ratio} color={st.color} />
          ) : (
            <Mark key={`live-${st.id}`} mark={st} ratio={ratio} />
          ),
        )}
        {ownDraft && <Mark mark={ownDraft} ratio={ratio} />}
        {laser.length > 0 && <LaserTrail points={laser} ratio={ratio} color={color} />}
      </svg>
      {typing && (
        <TextEntry
          x={typing.x}
          y={typing.y}
          value={typing.value}
          color={color}
          pageWidth={svgRef.current?.clientWidth ?? 0}
          placeholder={t("panels.typeHere")}
          onChange={(value) => setTyping((cur) => (cur ? { ...cur, value } : cur))}
          onDone={commitText}
          onCancel={() => setTyping(null)}
        />
      )}
    </>
  );
}

/** One finished (or in-progress) mark, by kind. */
function Mark({ mark, ratio }: { mark: Draft | Stroke; ratio: number }) {
  const { tool, color, width, points } = mark;
  const line = {
    fill: "none",
    stroke: color,
    strokeWidth: width,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (tool === "pen" || tool === "highlighter") {
    const highlighter = tool === "highlighter";
    return (
      <path
        d={strokePath(points, ratio)}
        {...line}
        strokeOpacity={highlighter ? HIGHLIGHTER_OPACITY : 1}
        // Multiply keeps the text under a highlighter dark instead of washing it out.
        style={highlighter ? { mixBlendMode: "multiply" } : undefined}
      />
    );
  }
  if (tool === "line" || tool === "arrow") {
    return (
      <g>
        <path d={strokePath(points, ratio)} {...line} />
        {tool === "arrow" && <path d={arrowHead(points, ratio, width)} {...line} />}
      </g>
    );
  }
  if (tool === "rect") {
    const b = shapeBox(points, ratio);
    return <rect x={b.x} y={b.y} width={b.w} height={b.h} {...line} />;
  }
  if (tool === "ellipse") {
    const b = shapeBox(points, ratio);
    return <ellipse cx={b.x + b.w / 2} cy={b.y + b.h / 2} rx={b.w / 2} ry={b.h / 2} {...line} />;
  }
  if (tool === "text" && "text" in mark && mark.text) {
    return (
      <text
        x={points[0]}
        y={points[1] * ratio}
        fontSize={width}
        fill={color}
        dominantBaseline="hanging"
        fontFamily="system-ui, sans-serif"
        fontWeight={600}
        style={{ whiteSpace: "pre" }}
      >
        {mark.text}
      </text>
    );
  }
  if (tool === "stamp" && "stamp" in mark && mark.stamp) {
    return <StampGlyph kind={mark.stamp} x={points[0]} y={points[1] * ratio} size={width} color={color} />;
  }
  return null;
}

const STAMP_ICONS = { check: Check, cross: X, star: Star, heart: Heart, question: CircleHelp } as const;

/** A stamp: an icon centred on its point. Drawn, not typed, so it looks the same on every device. */
export function StampGlyph({ kind, x, y, size, color }: { kind: StampKind; x: number; y: number; size: number; color: string }) {
  const Icon = STAMP_ICONS[kind];
  const filled = kind === "star" || kind === "heart";
  return (
    <Icon
      x={x - size / 2}
      y={y - size / 2}
      width={size}
      height={size}
      color={color}
      fill={filled ? color : "none"}
      strokeWidth={2.5}
    />
  );
}

/** A laser: a bright dot with a short fading tail behind it. */
function LaserTrail({ points, ratio, color }: { points: number[]; ratio: number; color: string }) {
  const n = points.length / 2;
  if (n === 0) return null;
  const x = points[(n - 1) * 2];
  const y = points[(n - 1) * 2 + 1] * ratio;
  const tail = points.slice(Math.max(0, (n - 12) * 2));
  return (
    <g style={{ pointerEvents: "none" }}>
      {tail.length >= 4 && (
        <path
          d={strokePath(tail, ratio)}
          fill="none"
          stroke={color}
          strokeWidth={STROKE_WIDTH.laser * 0.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeOpacity={0.35}
        />
      )}
      <circle cx={x} cy={y} r={STROKE_WIDTH.laser * 1.6} fill={color} fillOpacity={0.25} />
      <circle cx={x} cy={y} r={STROKE_WIDTH.laser * 0.7} fill={color} />
    </g>
  );
}

/**
 * Where text is typed before it becomes a mark: a borderless input at the
 * clicked spot, in the mark's own colour and size. Enter, clicking the page or
 * picking another tool keeps it; Escape drops it.
 */
function TextEntry({
  x,
  y,
  value,
  color,
  pageWidth,
  placeholder,
  onChange,
  onDone,
  onCancel,
}: {
  x: number;
  y: number;
  value: string;
  color: string;
  pageWidth: number;
  placeholder: string;
  onChange: (value: string) => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const doneRef = useRef(false);
  useEffect(() => {
    // After the click that opened it has finished, or it steals focus back.
    const id = window.setTimeout(() => ref.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);
  const finish = (keep: boolean) => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (keep) onDone();
    else onCancel();
  };
  return (
    <input
      ref={ref}
      type="text"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      maxLength={MAX_TEXT_LENGTH}
      placeholder={placeholder}
      className="absolute z-10 min-w-[8rem] border-0 border-b border-dashed bg-transparent p-0 font-semibold outline-none placeholder:text-slate-400"
      style={{
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        color,
        borderColor: color,
        fontSize: Math.max(10, STROKE_WIDTH.text * pageWidth),
        lineHeight: 1.2,
        fontFamily: "system-ui, sans-serif",
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(true);
        else if (e.key === "Escape") finish(false);
        // Typing must not reach the meeting's keyboard shortcuts.
        e.stopPropagation();
      }}
      onBlur={() => finish(true)}
    />
  );
}
